import { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';

/**
 * Drafting crosshair overlay for the 2D workspace.
 *
 * CURSORSIZE sysvar parity: the full crosshair length is a percentage of the
 * viewport (1-100). The overlay is pointer-transparent so it never steals
 * canvas events; only the runtime position is an inline style (allowed for
 * per-frame values), while line styling lives in theme tokens.
 */
export function DraftingCrosshair() {
    const cursorSize = useSystemVarsStore((s) => s.CURSORSIZE);
    const wrapRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const [size, setSize] = useState({ w: 0, h: 0 });

    // Track the pointer within the viewport container (capture phase so the
    // canvas listeners keep receiving their own events untouched).
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        const onMove = (e: PointerEvent) => {
            const rect = el.getBoundingClientRect();
            if (
                e.clientX < rect.left || e.clientX > rect.right ||
                e.clientY < rect.top || e.clientY > rect.bottom
            ) {
                setPos(null);
                return;
            }
            setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        };
        const onLeave = () => setPos(null);
        // The mount effect runs after React attaches refs, so `el` is valid.
        window.addEventListener('pointermove', onMove, true);
        el.addEventListener('pointerleave', onLeave);
        return () => {
            ro.disconnect();
            window.removeEventListener('pointermove', onMove, true);
            el.removeEventListener('pointerleave', onLeave);
        };
    }, []);

    const arm = (Math.min(size.w, size.h) * Math.max(1, Math.min(100, cursorSize))) / 200;
    if (!pos || arm < 2) return null;

    return (
        <Box ref={wrapRef} position="absolute" inset="0" pointerEvents="none" zIndex="1">
            <Box
                position="absolute"
                left={`${pos.x - arm}px`}
                top={`${pos.y - 0.5}px`}
                w={`${arm * 2}px`}
                h="1px"
                bg="rgba(255,255,255,0.55)"
            />
            <Box
                position="absolute"
                left={`${pos.x - 0.5}px`}
                top={`${pos.y - arm}px`}
                w="1px"
                h={`${arm * 2}px`}
                bg="rgba(255,255,255,0.55)"
            />
        </Box>
    );
}
